import { useEffect, useRef, useState } from 'react';
import './crop.css';

function ArrowLeftIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M19 12H5M11 6l-6 6 6 6" stroke="#ffffff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path d="M5 13l4 4L19 7" stroke="#ffffff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const MIN_CROP_SIZE = 80;
const MAX_OUTPUT_SIZE = 1200;

// left/top are percentages of the crop-selection box itself, so handles stay
// pinned to its corners/edges however that box is positioned or sized.
const HANDLES = [
  { name: 'top-left', left: 0, top: 0, cursor: 'nwse-resize', kind: 'corner' },
  { name: 'top', left: 50, top: 0, cursor: 'ns-resize', kind: 'edge-h' },
  { name: 'top-right', left: 100, top: 0, cursor: 'nesw-resize', kind: 'corner' },
  { name: 'left', left: 0, top: 50, cursor: 'ew-resize', kind: 'edge-v' },
  { name: 'right', left: 100, top: 50, cursor: 'ew-resize', kind: 'edge-v' },
  { name: 'bottom-left', left: 0, top: 100, cursor: 'nesw-resize', kind: 'corner' },
  { name: 'bottom', left: 50, top: 100, cursor: 'ns-resize', kind: 'edge-h' },
  { name: 'bottom-right', left: 100, top: 100, cursor: 'nwse-resize', kind: 'corner' },
];

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function clampCropToFrame(rect, frame) {
  const width = Math.max(Math.min(rect.width, frame.w), Math.min(MIN_CROP_SIZE, frame.w));
  const height = Math.max(Math.min(rect.height, frame.h), Math.min(MIN_CROP_SIZE, frame.h));
  const x = clamp(rect.x, 0, Math.max(0, frame.w - width));
  const y = clamp(rect.y, 0, Math.max(0, frame.h - height));
  return { x, y, width, height };
}

export default function Crop({ image, onCancel, onDone }) {
  const frameRef = useRef(null);
  const imgRef = useRef(null);

  // Rapidly-changing gesture state lives in refs so tracking pointers never
  // forces a re-render; only zoom/pan/cropRect (the values that affect
  // layout) are state.
  const pointersRef = useRef(new Map()); // pointerId -> { x, y } in client coords
  const dragRef = useRef(null); // single-finger image-drag baseline
  const pinchRef = useRef(null); // two-finger pinch baseline
  const resizeRef = useRef(null); // active crop-handle resize baseline
  const cropInitializedRef = useRef(false);
  const prevFrameSizeRef = useRef({ w: 0, h: 0 });

  const [frameSize, setFrameSize] = useState({ w: 0, h: 0 });
  const [naturalSize, setNaturalSize] = useState(null);
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [interacting, setInteracting] = useState(false);
  const [cropRect, setCropRect] = useState(null);
  const [activeCursor, setActiveCursor] = useState(null);

  // React attaches its synthetic onWheel listener as passive, so
  // event.preventDefault() inside it silently fails (and warns) in real
  // browsers. Route wheel handling through a native, non-passive listener
  // instead, via a ref so the listener always calls the latest closure.
  const wheelHandlerRef = useRef(null);

  // Handle-resize deliberately avoids setPointerCapture: capturing on
  // .crop-window (an ancestor of the actual pointerdown target, the handle
  // span) while that handle's own position keeps changing as cropRect
  // updates causes Chromium to fire a spurious pointercancel partway through
  // the very next such gesture, silently aborting the resize. Tracking the
  // drag via temporary window-level listeners sidesteps that entirely and is
  // the standard robust pattern for this class of drag interaction. The
  // handlers are re-attached every render (they close over current state),
  // so route them through stable ref-backed wrappers for correct add/remove.
  const pointerMoveHandlerRef = useRef(null);
  const pointerUpHandlerRef = useRef(null);
  const windowPointerMove = useRef((event) => pointerMoveHandlerRef.current?.(event)).current;
  const windowPointerUp = useRef((event) => pointerUpHandlerRef.current?.(event)).current;

  useEffect(() => {
    return () => {
      window.removeEventListener('pointermove', windowPointerMove);
      window.removeEventListener('pointerup', windowPointerUp);
      window.removeEventListener('pointercancel', windowPointerUp);
    };
  }, [windowPointerMove, windowPointerUp]);

  useEffect(() => {
    const el = frameRef.current;
    if (!el) return undefined;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      setFrameSize({ w: rect.width, h: rect.height });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // The crop card's own shape matches the photo's aspect ratio (instead of
  // always being a square), contained within the available space via pure
  // CSS (see --photo-ratio in crop.css) so it can never grow past the
  // viewport and push the header/buttons off-screen.
  const photoRatio = naturalSize ? naturalSize.w / naturalSize.h : 1;

  useEffect(() => {
    const el = frameRef.current;
    if (!el) return undefined;
    const onNativeWheel = (event) => wheelHandlerRef.current?.(event);
    el.addEventListener('wheel', onNativeWheel, { passive: false });
    return () => el.removeEventListener('wheel', onNativeWheel);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setZoom(MIN_ZOOM);
    setPan({ x: 0, y: 0 });
    setNaturalSize(null);
    setCropRect(null);
    cropInitializedRef.current = false;
    pointersRef.current.clear();
    dragRef.current = null;
    pinchRef.current = null;
    resizeRef.current = null;
    setInteracting(false);
    setActiveCursor(null);

    // data: URLs can finish decoding before the <img> onLoad handler is even
    // attached, so the load event can be missed. decode() resolves correctly
    // whether the image is still loading or already complete.
    const img = imgRef.current;
    if (img) {
      img
        .decode()
        .then(() => {
          if (!cancelled) setNaturalSize({ w: img.naturalWidth, h: img.naturalHeight });
        })
        .catch(() => {});
    }

    return () => {
      cancelled = true;
    };
  }, [image]);

  // Initialize the crop rectangle once, as soon as both the image and the
  // viewport are measured: it starts covering the entire frame (i.e. the
  // whole uploaded photo is selected by default) rather than a centered,
  // margined subset.
  useEffect(() => {
    if (cropInitializedRef.current) return;
    if (!naturalSize || !frameSize.w || !frameSize.h) return;
    setCropRect({
      x: 0,
      y: 0,
      width: frameSize.w,
      height: frameSize.h,
    });
    cropInitializedRef.current = true;
  }, [naturalSize, frameSize.w, frameSize.h]);

  // If the viewport is resized/rotated after the crop rect exists, rescale it
  // proportionally to the new frame bounds (rather than just clamping it into
  // them) so a full-frame selection stays full-frame — and a partial one
  // keeps its relative position/size — instead of leaving a gap when the
  // frame grows after the initial measurement (e.g. once --photo-ratio kicks
  // in and the crop-window's own layout settles).
  useEffect(() => {
    if (!cropInitializedRef.current) {
      prevFrameSizeRef.current = frameSize;
      return;
    }
    const prevFrame = prevFrameSizeRef.current;
    setCropRect((prev) => {
      if (!prev) return prev;
      if (!prevFrame.w || !prevFrame.h) return clampCropToFrame(prev, frameSize);
      const scaleX = frameSize.w / prevFrame.w;
      const scaleY = frameSize.h / prevFrame.h;
      const scaled = {
        x: prev.x * scaleX,
        y: prev.y * scaleY,
        width: prev.width * scaleX,
        height: prev.height * scaleY,
      };
      return clampCropToFrame(scaled, frameSize);
    });
    prevFrameSizeRef.current = frameSize;
  }, [frameSize.w, frameSize.h]);

  const baseScale = naturalSize && frameSize.w && frameSize.h
    ? Math.max(frameSize.w / naturalSize.w, frameSize.h / naturalSize.h)
    : 1;
  const totalScale = baseScale * zoom;
  const isLoading = !naturalSize;

  function clampPan(next, scale) {
    if (!naturalSize) return next;
    const dispW = naturalSize.w * scale;
    const dispH = naturalSize.h * scale;
    const maxX = Math.max(0, (dispW - frameSize.w) / 2);
    const maxY = Math.max(0, (dispH - frameSize.h) / 2);
    return { x: clamp(next.x, -maxX, maxX), y: clamp(next.y, -maxY, maxY) };
  }

  function getImageBox(scale) {
    if (!naturalSize) return null;
    const dispW = naturalSize.w * scale;
    const dispH = naturalSize.h * scale;
    const left = frameSize.w / 2 - dispW / 2 + pan.x;
    const top = frameSize.h / 2 - dispH / 2 + pan.y;
    return { dispW, dispH, left, top };
  }

  function handleImageLoad() {
    const img = imgRef.current;
    if (!img) return;
    setNaturalSize({ w: img.naturalWidth, h: img.naturalHeight });
  }

  function setZoomClamped(nextZoom) {
    const z = clamp(nextZoom, MIN_ZOOM, MAX_ZOOM);
    setZoom(z);
    setPan((prev) => clampPan(prev, baseScale * z));
  }

  // ---------- Pinch helpers (image zoom only — never touches cropRect) ----------

  function startPinch(pts) {
    const [p1, p2] = pts;
    const rect = frameRef.current ? frameRef.current.getBoundingClientRect() : { left: 0, top: 0 };
    const distance = Math.hypot(p2.x - p1.x, p2.y - p1.y);
    pinchRef.current = {
      initialDistance: distance,
      initialZoom: zoom,
      midpointX: (p1.x + p2.x) / 2 - rect.left,
      midpointY: (p1.y + p2.y) / 2 - rect.top,
      initialPanX: pan.x,
      initialPanY: pan.y,
    };
  }

  function updatePinch(pts) {
    const pinch = pinchRef.current;
    if (!pinch || !naturalSize || pinch.initialDistance <= 0) return;

    const [p1, p2] = pts;
    const distance = Math.hypot(p2.x - p1.x, p2.y - p1.y);
    const scaleRatio = distance / pinch.initialDistance;
    const newZoom = clamp(pinch.initialZoom * scaleRatio, MIN_ZOOM, MAX_ZOOM);

    const oldScale = baseScale * pinch.initialZoom;
    const newScale = baseScale * newZoom;

    // Where the pinch midpoint sits in natural-image coordinates, using the
    // image box as it was when the pinch started (getImageBox()'s formula).
    const oldDispW = naturalSize.w * oldScale;
    const oldDispH = naturalSize.h * oldScale;
    const oldLeft = frameSize.w / 2 - oldDispW / 2 + pinch.initialPanX;
    const oldTop = frameSize.h / 2 - oldDispH / 2 + pinch.initialPanY;
    const imagePointX = (pinch.midpointX - oldLeft) / oldScale;
    const imagePointY = (pinch.midpointY - oldTop) / oldScale;

    // Solve for the pan that keeps that same natural-image point under the
    // (possibly moved) midpoint at the new scale.
    const newDispW = naturalSize.w * newScale;
    const newDispH = naturalSize.h * newScale;
    const newPanX = pinch.midpointX - frameSize.w / 2 + newDispW / 2 - imagePointX * newScale;
    const newPanY = pinch.midpointY - frameSize.h / 2 + newDispH / 2 - imagePointY * newScale;

    setZoom(newZoom);
    setPan(clampPan({ x: newPanX, y: newPanY }, newScale));
  }

  // ---------- Crop-handle resize (crop rect only — never touches zoom/pan) ----------

  function computeHorizontal(handle, dx, initial) {
    const { x: ix, width: iw } = initial;
    if (handle === 'left' || handle === 'top-left' || handle === 'bottom-left') {
      const rightEdge = ix + iw; // stays fixed while dragging the left edge
      let width = iw - dx;
      let x = ix + dx;
      if (width < MIN_CROP_SIZE) {
        width = MIN_CROP_SIZE;
        x = rightEdge - width;
      }
      if (x < 0) {
        x = 0;
        width = rightEdge - x;
      }
      return { x, width };
    }
    if (handle === 'right' || handle === 'top-right' || handle === 'bottom-right') {
      let width = iw + dx;
      if (width < MIN_CROP_SIZE) width = MIN_CROP_SIZE;
      const maxWidth = frameSize.w - ix;
      if (width > maxWidth) width = maxWidth;
      return { x: ix, width };
    }
    return { x: ix, width: iw };
  }

  function computeVertical(handle, dy, initial) {
    const { y: iy, height: ih } = initial;
    if (handle === 'top' || handle === 'top-left' || handle === 'top-right') {
      const bottomEdge = iy + ih; // stays fixed while dragging the top edge
      let height = ih - dy;
      let y = iy + dy;
      if (height < MIN_CROP_SIZE) {
        height = MIN_CROP_SIZE;
        y = bottomEdge - height;
      }
      if (y < 0) {
        y = 0;
        height = bottomEdge - y;
      }
      return { y, height };
    }
    if (handle === 'bottom' || handle === 'bottom-left' || handle === 'bottom-right') {
      let height = ih + dy;
      if (height < MIN_CROP_SIZE) height = MIN_CROP_SIZE;
      const maxHeight = frameSize.h - iy;
      if (height > maxHeight) height = maxHeight;
      return { y: iy, height };
    }
    return { y: iy, height: ih };
  }

  function updateResize(event) {
    const r = resizeRef.current;
    if (!r) return;
    const dx = event.clientX - r.startX;
    const dy = event.clientY - r.startY;
    const { x, width } = computeHorizontal(r.handle, dx, r.initial);
    const { y, height } = computeVertical(r.handle, dy, r.initial);
    setCropRect({ x, y, width, height });
  }

  // ---------- Pointer events (single dispatcher: handle vs. image drag/pinch) ----------

  function handlePointerDown(event) {
    if (!naturalSize) return;

    const handleEl = event.target.closest ? event.target.closest('[data-handle]') : null;
    if (handleEl && cropRect) {
      resizeRef.current = {
        pointerId: event.pointerId,
        handle: handleEl.getAttribute('data-handle'),
        startX: event.clientX,
        startY: event.clientY,
        initial: { ...cropRect },
      };
      const config = HANDLES.find((h) => h.name === resizeRef.current.handle);
      setActiveCursor(config ? config.cursor : null);
      setInteracting(true);
      window.addEventListener('pointermove', windowPointerMove);
      window.addEventListener('pointerup', windowPointerUp);
      window.addEventListener('pointercancel', windowPointerUp);
      return;
    }

    event.currentTarget.setPointerCapture(event.pointerId);
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const pts = Array.from(pointersRef.current.values());

    if (pts.length === 1) {
      pinchRef.current = null;
      dragRef.current = { startX: event.clientX, startY: event.clientY, panX: pan.x, panY: pan.y };
    } else if (pts.length === 2) {
      // Second finger just landed: cancel the one-finger drag and start a pinch.
      dragRef.current = null;
      startPinch(pts);
    }
    setInteracting(true);
  }

  function handlePointerMove(event) {
    if (resizeRef.current && event.pointerId === resizeRef.current.pointerId) {
      updateResize(event);
      return;
    }

    if (!pointersRef.current.has(event.pointerId)) return;
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const pts = Array.from(pointersRef.current.values());

    if (pts.length >= 2 && pinchRef.current) {
      updatePinch(pts);
    } else if (pts.length === 1 && dragRef.current) {
      const dx = event.clientX - dragRef.current.startX;
      const dy = event.clientY - dragRef.current.startY;
      setPan(clampPan({ x: dragRef.current.panX + dx, y: dragRef.current.panY + dy }, totalScale));
    }
  }

  function handlePointerUp(event) {
    if (resizeRef.current && event.pointerId === resizeRef.current.pointerId) {
      resizeRef.current = null;
      setActiveCursor(null);
      setInteracting(false);
      window.removeEventListener('pointermove', windowPointerMove);
      window.removeEventListener('pointerup', windowPointerUp);
      window.removeEventListener('pointercancel', windowPointerUp);
      return;
    }

    pointersRef.current.delete(event.pointerId);
    const pts = Array.from(pointersRef.current.values());

    if (pts.length === 0) {
      dragRef.current = null;
      pinchRef.current = null;
      setInteracting(false);
    } else if (pts.length === 1) {
      // Back down to one finger: drop the pinch and re-baseline the drag from
      // the remaining finger's current position so the image doesn't jump.
      pinchRef.current = null;
      dragRef.current = { startX: pts[0].x, startY: pts[0].y, panX: pan.x, panY: pan.y };
    } else {
      // Still 2+ fingers (a third finger lifted): re-baseline the pinch from
      // the current state of the remaining pair, again to avoid a jump.
      startPinch(pts.slice(0, 2));
    }
  }

  function handleWheel(event) {
    if (!naturalSize) return;
    event.preventDefault();
    setZoomClamped(zoom - event.deltaY * 0.0015);
  }

  // Keep the native listener (registered once, above) calling this render's
  // latest closure without re-attaching the DOM listener every render.
  wheelHandlerRef.current = handleWheel;
  pointerMoveHandlerRef.current = handlePointerMove;
  pointerUpHandlerRef.current = handlePointerUp;

  function handleUsePhoto() {
    const img = imgRef.current;
    const box = getImageBox(totalScale);
    if (!img || !box || !naturalSize || !cropRect) return;

    // Convert the crop rectangle from viewport pixels to natural-image pixels.
    let srcX = (cropRect.x - box.left) / totalScale;
    let srcY = (cropRect.y - box.top) / totalScale;
    let srcW = cropRect.width / totalScale;
    let srcH = cropRect.height / totalScale;

    if (srcX < 0) {
      srcW += srcX;
      srcX = 0;
    }
    if (srcY < 0) {
      srcH += srcY;
      srcY = 0;
    }
    if (srcX + srcW > naturalSize.w) srcW = naturalSize.w - srcX;
    if (srcY + srcH > naturalSize.h) srcH = naturalSize.h - srcY;
    srcW = Math.max(0, srcW);
    srcH = Math.max(0, srcH);

    // Output preserves the crop rectangle's aspect ratio instead of a fixed
    // square, capped to MAX_OUTPUT_SIZE on the longer side.
    const aspect = cropRect.width / cropRect.height;
    let outputWidth;
    let outputHeight;
    if (aspect >= 1) {
      outputWidth = MAX_OUTPUT_SIZE;
      outputHeight = Math.round(MAX_OUTPUT_SIZE / aspect);
    } else {
      outputHeight = MAX_OUTPUT_SIZE;
      outputWidth = Math.round(MAX_OUTPUT_SIZE * aspect);
    }
    outputWidth = Math.max(1, outputWidth);
    outputHeight = Math.max(1, outputHeight);

    const canvas = document.createElement('canvas');
    canvas.width = outputWidth;
    canvas.height = outputHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, srcX, srcY, srcW, srcH, 0, 0, outputWidth, outputHeight);

    onDone?.(canvas.toDataURL('image/jpeg', 0.92));
  }

  const imageBox = getImageBox(totalScale);

  return (
    <div className="crop-screen">
      <div className="crop-frame-outer">
        <div className="crop-topbar">
          <button type="button" className="crop-back-btn" aria-label="Cancel crop" onClick={onCancel}>
            <ArrowLeftIcon />
          </button>
          <div className="crop-title-block">
            <span className="crop-eyebrow">Photo editor</span>
            <p className="crop-title">Adjust your photo</p>
          </div>
          <span className="crop-topbar-spacer" aria-hidden="true" />
        </div>

        <div className="crop-viewport">
          <div
            ref={frameRef}
            className="crop-window"
            style={{
              '--photo-ratio': photoRatio,
              ...(activeCursor ? { cursor: activeCursor } : null),
            }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
          >
            {/* Clipped to the rounded card: only the image (and its loading
                state) needs to be cropped to the viewport shape. */}
            <div className="crop-clip">
              <img
                ref={imgRef}
                src={image}
                alt="Crop your selfie"
                className="crop-image"
                draggable={false}
                onLoad={handleImageLoad}
                style={
                  imageBox
                    ? {
                        width: `${imageBox.dispW}px`,
                        height: `${imageBox.dispH}px`,
                        left: `${imageBox.left}px`,
                        top: `${imageBox.top}px`,
                      }
                    : { opacity: 0 }
                }
              />

              {isLoading && (
                <div className="crop-loading">
                  <span className="crop-loading-spinner" />
                  <span className="crop-loading-text">Loading photo&hellip;</span>
                </div>
              )}
            </div>

            {cropRect && (
              <>
                {/* Dimming shadow lives in its own clipped layer so it never
                    bleeds past the rounded card. */}
                <div className="crop-dim" aria-hidden="true">
                  <span
                    className="crop-dim-shadow"
                    style={{
                      left: `${cropRect.x}px`,
                      top: `${cropRect.y}px`,
                      width: `${cropRect.width}px`,
                      height: `${cropRect.height}px`,
                    }}
                  />
                </div>

                {/* Selection border + grid + handles are deliberately NOT
                    clipped, so handles stay fully grabbable even when the
                    crop rect touches the viewport edge. */}
                <div
                  className="crop-selection"
                  style={{
                    left: `${cropRect.x}px`,
                    top: `${cropRect.y}px`,
                    width: `${cropRect.width}px`,
                    height: `${cropRect.height}px`,
                  }}
                >
                  <span className={'crop-grid' + (interacting ? ' crop-grid-active' : '')} aria-hidden="true" />

                  {HANDLES.map((h) => (
                    <span
                      key={h.name}
                      className={`crop-handle crop-handle-${h.kind}`}
                      data-handle={h.name}
                      style={{ left: `${h.left}%`, top: `${h.top}%`, cursor: h.cursor }}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="crop-actions">
          <button type="button" className="crop-btn-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="crop-btn-primary"
            onClick={handleUsePhoto}
            disabled={isLoading}
          >
            <span className="crop-btn-icon">
              <CheckIcon />
            </span>
            Use Photo
          </button>
        </div>
      </div>
    </div>
  );
}
